/**
 * Object for handling touch interactions related to the cropper
 *
 */
import SelectorActions from '../../../actions/selector-actions'

// Private variables
var _activePinch = false;

var TouchHandlers = {
	/**
	 * This function is the initial handler of any touch/gesture interaction with the cropper.
	 * From here the functions handlePan or handlePinch are called depending on what type of
	 * interaction that triggered this function.
	 */
	handleTouchInteraction: function(event, instance) {
		// Case a pan interaction
		switch(event.type) {
			case 'panstart':
			case 'panmove':
			case 'panend' :
				this.handlePan(event, instance);
				break;
			// Case a pinch interaction
			case 'pinchin':
			case 'pinchout':
			case 'pinchstart':
			case 'pinchend' :
				this.handlePinch(event, instance);
				break;
		}
	},

	/**
	 * This function handle the pan-related events: panstart, panmove, panend.
	 *
	 * @param event EventObject - reference to the HammerJS event that triggered the pan event
	 * @param instance ReactClass - reference to the instance of DetailedCropper
	 *
	 */
	handlePan : function(event, instance) {
		var options = instance.props.item.options;
		var scale = options.scale;
		var bounds = instance.boundaryPoints;
		var dx = event.deltaX + instance.cropPosition.x;
		var dy = event.deltaY + instance.cropPosition.y;

		switch(event.type) {
			case 'panstart':
				// Store starting crop position.
				instance.cropPosition = instance.getXYPosition(
					instance.state.options.position.percentageX,
					instance.state.options.position.percentageY,
					instance.selectionImage
				);
				break;
			case 'panmove':
				// Check if new position is within the boundaries
				var position = TouchHandlers.validateImagePosition(instance, dx, dy, scale);
				dx = position.x;
				dy = position.y;

				var percentage = instance.getXYPercentage(dx, dy, instance.selectionImage);
				options.position.percentageX = percentage.percentageX;
				options.position.percentageY = percentage.percentageY;

				instance.setState({options: options });

				instance.lastValidXY.x = dx;
				instance.lastValidXY.y = dy;

				break;

			case 'panend':
				instance.cropPosition.x = instance.lastValidXY.x;
				instance.cropPosition.y = instance.lastValidXY.y;

				instance.saveCropPosition(scale, instance.cropPosition.x, instance.cropPosition.y, instance.selectionImage);
				break;
		}
	},

	/**
	 * This function handles pinch-interactions on a touch device. These interactions are used to zoom the crop of an image.
	 * This function is called via the handleTouchInteraction function.
	 * @param event Eventobject
	 * @param instance ReactClass
	 */
	handlePinch: function(event, instance) {
		var x, y, newDPI,
			scale = instance.preScale,
			item = instance.props.item;

		switch(event.type) {
			case 'pinchstart' :
				// Store starting crop position.
				instance.cropPosition = instance.getXYPosition(
					instance.state.options.position.percentageX,
					instance.state.options.position.percentageY,
					instance.selectionImage
				);
				_activePinch = true;
				break;
			case 'pinchin':
			case 'pinchout':
				if(_activePinch) {
					var newScale = event.scale * scale;
					x = event.center.x;
					y = event.center.y;
					// Make sure that the scale is never below the scale needed to fill the container
					if(newScale < 1.0) newScale = 1.0;

					newDPI = item.getDPI(newScale);
					// if newDPI too low to allow further zooming
					if(newDPI < 151) {
						// @todo warning to user that not allowed to zoom further
					}
					if(newDPI > 151 && newDPI < 300) {
						// Add warning
						if(!item.getLowResWarning()) {
							SelectorActions.setLowResWarning(item.getId());
						}
					}
					else if(newDPI > 300) {
						if(item.getLowResWarning()) {
							SelectorActions.removeLowResWarning(item.getId());
						}
					}
					if(newDPI > 150) {
						TouchHandlers.zoomAtPoint(x, y, newScale, instance);
					}
				}
				break;
			case 'pinchend' :
				instance.lastScale = instance.state.item.options.scale;
				instance.preScale = instance.state.item.options.scale;
				instance.cropPosition.x = instance.lastValidXY.x;
				instance.cropPosition.y = instance.lastValidXY.y;
				_activePinch = false;
				// Save updated scale in the orderStore
				instance.saveCropPosition(instance.lastScale, instance.cropPosition.x, instance.cropPosition.y, instance.selectionImage);
				break;
		}
	},

	/**
	 * x and y should refer to a point within the selection component
	 *
	 */
	zoomAtPoint: function (x, y, scale, instance) {
		if (instance) {
			if (instance.selectionImage) {
				var selectionImageRect = instance.selectionImage.getBoundingClientRect(),
					x = x - selectionImageRect.left || selectionImageRect.x,
					y = y - selectionImageRect.top || selectionImageRect.y,
					imagePosition = instance.props.item.options.position,
					positionXY = instance.getXYPosition(imagePosition.percentageX, imagePosition.percentageY, instance.selectionImage),
					newXPosition = x * scale / instance.lastScale,
					newYPosition = y * scale / instance.lastScale,
					diffx = parseFloat(positionXY.x) + x - newXPosition,
					diffy = parseFloat(positionXY.y) + y - newYPosition;


				var validatedPosition = TouchHandlers.validateImagePosition(instance, diffx, diffy, scale),
					item = instance.state.item,
					position = {
						x : validatedPosition.x,
						y: validatedPosition.y
					},
					options = {
						scale: scale,
						position: position,
					};

				item.options.scale = options.scale;
				item.options.position = options.position;

				instance.setState({item: item});

				instance.lastScale = scale;
				instance.cropPosition.x = validatedPosition.x;
				instance.cropPosition.y = validatedPosition.y;
				instance.lastValidXY.x = validatedPosition.x;
				instance.lastValidXY.y = validatedPosition.y;

				if(!instance._activePinch) {
					// Update the x & y pixel position after zoom is done
					instance.saveCropPosition(scale, instance.cropPosition.x, instance.cropPosition.y, instance.selectionImage);
				}
			}
		}
	},

	/**
	 * Function for validating the image's position
	 *
	 */
	validateImagePosition: function(instance, x, y, scale) {
		var bounds = instance.boundaryPoints;

		if((x + (bounds.x2 * scale )) < (bounds.x1 )) {
        	x = (Math.abs(bounds.x1 - (bounds.x2 * scale))) * -1;
        }
		if(((x + (bounds.x2 * scale ))) > (bounds.x2 * scale)) {
			x = 0;
		}
		//Use bounds to check if pan on y-axis is allowed
        if((y + (bounds.y2 * scale)) < bounds.y1) {
        	y = (Math.abs(bounds.y1 - (bounds.y2 * scale) )) * -1;
		}
        if ((y + bounds.y2 > bounds.y2)) {
        	y = 0;
		}

		return {'x' : x, 'y' : y, 'scale': scale };
	}
};

export default TouchHandlers;
